import React from "react";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";

import SchoolIcon from "@mui/icons-material/School";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GroupsIcon from "@mui/icons-material/Groups";
import PsychologyIcon from "@mui/icons-material/Psychology";
import MenuBookIcon from "@mui/icons-material/MenuBook";

import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

import WeekelyTest from "./WeekelyTest";
import LearningBenefits from "./LearningBenefits";

const data = [
  {
    title: "Our Mission",
    icon: <SchoolIcon />,
    color: ["#FF6B00", "#FF9A3D"], // orange
    text: "At VedicWin, our mission is to provide high-quality, affordable, and personalized education that helps every student realize their full potential. We focus on building strong conceptual foundations in Mathematics, Science, English, Hindi, Sanskrit, and Regional Languages while encouraging curiosity, creativity, confidence, and critical thinking. Through dedicated mentoring and continuous support, we prepare students for academic excellence and lifelong learning.",
  },

  {
    title: "Our Vision",
    icon: <VisibilityIcon />,
    color: ["#2E7DFF", "#5FA8FF"], // blue
    text: "Our vision is to become a trusted center of excellence in education by nurturing confident, knowledgeable, and responsible students. We aspire to create an inspiring learning environment where every child develops strong academic skills, leadership qualities, and the confidence to face future educational and career challenges with determination.",
  },

  {
    title: "Why Choose Us",
    icon: <GroupsIcon />,
    color: ["#00B894", "#55E6C1"], // teal/green
    text: "We offer experienced faculty, small batch sizes, personalized attention, regular assessments, doubt-clearing sessions, weekly tests, and flexible online as well as offline classes. Our teaching methodology is designed to strengthen concepts rather than memorization, helping students achieve outstanding academic performance in CBSE, ICSE, and State Board examinations.",
  },

  {
    title: "Our Teaching Methodology",
    icon: <PsychologyIcon />,
    color: ["#8E44FF", "#B388FF"], // purple
    text: "We believe learning becomes meaningful when students truly understand concepts. Our interactive teaching approach includes practical examples, visual learning, classroom discussions, problem-solving sessions, regular assignments, and continuous practice. This ensures students develop confidence, analytical thinking, and long-term academic success.",
  },

  {
    title: "Student Development",
    icon: <MenuBookIcon />,
    color: ["#FF3D71", "#FF7A9C"], // pink/red
    text: "Our focus extends beyond classroom learning. We help students improve communication skills, time management, logical reasoning, creativity, discipline, teamwork, and self-confidence. By encouraging curiosity and independent thinking, we prepare students to succeed both academically and personally.",
  },

  {
    title: "Our Impact",
    icon: <EmojiEventsIcon />,
    color: ["#FFC107", "#FFD95E"], // gold/yellow
    text: "Over the years, hundreds of students have achieved remarkable academic improvement through our guidance. More than 95% of our students have improved their grades, strengthened their concepts, and gained confidence. Many have secured top ranks in school examinations, Olympiads, scholarship tests, and other academic competitions.",
  },
];

// Motion variants
const headingVariants = {
  hidden: { opacity: 0, y: -30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const gridVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.55, ease: "easeOut" },
  },
};

const MotionCard = motion(Card);
const MotionBox = motion(Box);

export default function About() {
  return (
    <>
      <Box
        sx={{
          py: { xs: 7, md: 6 },
          background:
            "linear-gradient(180deg,#ffffff 0%,#fff8f2 100%)",
        }}
      >
        <Container maxWidth="xl">
          {/* Heading */}

          <MotionBox
            textAlign="center"
            mb={6}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={headingVariants}
          >
            <Typography
              sx={{
                color: "#FF6B00",
                letterSpacing: 2,
                mb: 1,
                fontSize: "14px",
                fontWeight: "bold", // or 800
              }}
            >
              ABOUT US
            </Typography>

            <Typography
              sx={{
                fontSize: {
                  xs: "25px",
                  sm: "30px",
                  md: "36px",
                  lg: "42px",
                },
                fontWeight: 800,
                color: "#121212",
                mb: 2,
              }}
            >
              Empowering Students Through Quality Education
            </Typography>

            <Typography
              sx={{
                maxWidth: "700px",
                color: "#666",
                fontSize: "15px",
                lineHeight: 1.8,
              }}
            >
            </Typography>
          </MotionBox>

          {/* Cards */}

          <MotionBox
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2,1fr)",
                md: "repeat(3,1fr)",
              },
              gap: { xs: 2.5, sm: 3, md: 3.5, lg: 4 },
              mt: { xs: 4, md: 8 },
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            variants={gridVariants}
          >
            {data.map((item, index) => (
              <MotionCard
                key={index}
                variants={cardVariants}
                whileHover={{
                  y: -8,
                  boxShadow: `0px 15px 35px ${item.color[0]}33`,
                }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                sx={{
                  height: "100%",
                  borderRadius: "24px",
                  border: "1px solid #FFE5D0",
                  boxShadow: "none",
                }}
              >
                <CardContent
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    textAlign: "center",
                  }}
                >
                  {/* Icon */}

                  <motion.div
                    animate={{ y: [0, -8, 0] }}
                    transition={{
                      duration: 3,
                      ease: "easeInOut",
                      repeat: Infinity,
                    }}
                    style={{
                      width: 75,
                      height: 75,
                      margin: "0 auto 16px",
                      borderRadius: "20px",
                      background: `linear-gradient(135deg,${item.color[0]},${item.color[1]})`,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        "& svg": {
                          fontSize: 40,
                        },
                      }}
                    >
                      {item.icon}
                    </Box>
                  </motion.div>

                  <Typography
                    sx={{
                      fontSize: "22px",
                      fontWeight: 800,
                      color: "#121212",
                      mb: 2,
                    }}
                  >
                    {item.title}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#666",
                      fontSize: "14px",
                      lineHeight: 1.8,
                    }}
                  >
                    {item.text}
                  </Typography>
                </CardContent>
              </MotionCard>
            ))}
          </MotionBox>
        </Container>
      </Box>

      <WeekelyTest/>
      <LearningBenefits/>
    </>
  );
}